import { smoothstep } from "../math.js";
import type { HalftoneData } from "../data/types.js";

/** A new `HalftoneData` with only the dots at `indices` (raw indices into the source arrays),
    or with every dot but those, in their original order. The face leaves out the butterfly's
    dots, and the butterfly keeps only those, as its own layer. `data` isn't touched. */
export function selectDots(
  data: HalftoneData,
  indices: ReadonlySet<number>,
  mode: "keep" | "exclude",
): HalftoneData {
  const keep = mode === "keep";
  const x: number[] = [];
  const y: number[] = [];
  const r: number[] = [];
  const colorIndex: number[] = [];
  for (let i = 0; i < data.n; i++) {
    if (indices.has(i) !== keep) continue;
    x.push(data.x[i] ?? 0);
    y.push(data.y[i] ?? 0);
    r.push(data.r[i] ?? 0);
    colorIndex.push(data.colorIndex[i] ?? 0);
  }
  return {
    w: data.w,
    h: data.h,
    n: x.length,
    x: Float32Array.from(x),
    y: Float32Array.from(y),
    r: Float32Array.from(r),
    colorIndex: Uint8Array.from(colorIndex),
    colors: data.colors,
  };
}

/** The median distance from each dot to its nearest neighbor, or `fallback` when there are
    fewer than two dots. A spacing of 0 (two dots on the same spot) falls back too. */
export function medianNearestSpacing(
  xs: Float32Array | readonly number[],
  ys: Float32Array | readonly number[],
  fallback = 8,
): number {
  const count = xs.length;
  if (count < 2) return fallback;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let index = 0; index < count; index++) {
    const x = xs[index] ?? 0;
    const y = ys[index] ?? 0;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  const spanX = maxX - minX;
  const spanY = maxY - minY;
  if (spanX === 0 && spanY === 0) return fallback;
  // The face has thousands of dots, so instead of checking every other dot, each dot searches
  // a grid of cells about one typical spacing wide. It goes ring by ring around its own cell,
  // until no cell further out could hold a closer dot. (A dot past `ring` rings is more than
  // `ring` cells away on at least one axis.)
  const cell = Math.max(Math.sqrt((spanX * spanY) / count), (spanX + spanY) / count);
  const columns = Math.floor(spanX / cell) + 1;
  const rows = Math.floor(spanY / cell) + 1;
  const column = (x: number): number => Math.floor((x - minX) / cell);
  const row = (y: number): number => Math.floor((y - minY) / cell);
  const cells = new Map<number, number[]>();
  for (let index = 0; index < count; index++) {
    const key = column(xs[index] ?? 0) * rows + row(ys[index] ?? 0);
    const members = cells.get(key);
    if (members) members.push(index);
    else cells.set(key, [index]);
  }
  const nearest = new Float64Array(count);
  for (let index = 0; index < count; index++) {
    const x = xs[index] ?? 0;
    const y = ys[index] ?? 0;
    const cx = column(x);
    const cy = row(y);
    let nearestSquared = Infinity;
    for (let ring = 0; ring <= columns + rows; ring++) {
      for (let gx = cx - ring; gx <= cx + ring; gx++) {
        if (gx < 0 || gx >= columns) continue;
        // Only the ring's own cells: its top and bottom rows, and its two side columns.
        const step = gx === cx - ring || gx === cx + ring ? 1 : 2 * ring;
        for (let gy = cy - ring; gy <= cy + ring; gy += Math.max(step, 1)) {
          if (gy < 0 || gy >= rows) continue;
          for (const neighbor of cells.get(gx * rows + gy) ?? []) {
            if (neighbor === index) continue;
            const dx = (xs[neighbor] ?? 0) - x;
            const dy = (ys[neighbor] ?? 0) - y;
            nearestSquared = Math.min(nearestSquared, dx * dx + dy * dy);
          }
        }
      }
      if (nearestSquared <= (ring * cell) ** 2) break;
    }
    nearest[index] = Math.sqrt(nearestSquared);
  }
  return nearest.toSorted()[count >> 1] || fallback;
}

/** How a group's dots fade out at the print's outline (`outlineFade`). Each dot counts the
    print's dots within `reach` spacings of it, as a fraction of what the group's inner dots
    count. The opacity runs from `least` at fraction `from` to full at fraction `to`, so the
    fade reaches about `reach` rows in from the outline. */
export interface OutlineFadeShape {
  reach: number;
  least: number;
  from: number;
  to: number;
}

/** The butterfly's fade. I picked it out of two variants. */
export const OUTLINE_FADE: Readonly<OutlineFadeShape> = Object.freeze({
  reach: 3,
  least: 0.15,
  from: 0.5,
  to: 0.95,
});

/** The face's fade: one row narrower than the butterfly's, which looked too soft on the face. */
export const FACE_OUTLINE_FADE: Readonly<OutlineFadeShape> = Object.freeze({
  ...OUTLINE_FADE,
  reach: 2,
});
// Rows per column in a cell key: more than any artwork has, so keys never collide.
const CELL_KEY_ROWS = 1 << 20;
// Samples along each side of the square around a dot's reach (`insideShare`).
const SHARE_SAMPLES = 16;

// The fraction of a dot's reach that's inside the artwork. The artwork's box cuts off the
// print, but that cut isn't the print's outline (the face's edge tapers away there,
// `FACE_EDGE`), so a dot near the box only counts the part of its reach inside it.
function insideShare(x: number, y: number, reach: number, w: number, h: number): number {
  if (x >= reach && y >= reach && x <= w - reach && y <= h - reach) return 1;
  let inReach = 0;
  let inside = 0;
  for (let a = 0; a < SHARE_SAMPLES; a++) {
    for (let b = 0; b < SHARE_SAMPLES; b++) {
      const ox = (((a + 0.5) / SHARE_SAMPLES) * 2 - 1) * reach;
      const oy = (((b + 0.5) / SHARE_SAMPLES) * 2 - 1) * reach;
      if (ox * ox + oy * oy > reach * reach) continue;
      inReach++;
      if (x + ox >= 0 && x + ox <= w && y + oy >= 0 && y + oy <= h) inside++;
    }
  }
  return inside / inReach;
}

/** Each kept dot's opacity, in `selectDots` "keep" order: 1 inside the print, less toward
    its outline. That way a layer cut out of the print has no hard outer edge, but stays solid
    where it meets the rest of the print. Where the artwork's box cuts the print, the dots stay
    solid too. */
export function outlineFade(
  data: HalftoneData,
  keep: ReadonlySet<number>,
  shape: Readonly<OutlineFadeShape> = OUTLINE_FADE,
): Float32Array {
  const kept = [...keep].filter((i) => i >= 0 && i < data.n).sort((a, b) => a - b);
  const fade = new Float32Array(kept.length).fill(1);
  if (kept.length < 2) return fade;
  const spacing = medianNearestSpacing(
    Float32Array.from(kept, (i) => data.x[i] ?? 0),
    Float32Array.from(kept, (i) => data.y[i] ?? 0),
  );
  // A hair over the reach, so lattice dots at exactly that distance still count.
  const reach = shape.reach * spacing * 1.01;
  const reachSquared = reach * reach;
  // Cells one reach wide, with number keys (the face has thousands of dots). A dot's neighbors
  // within reach are all in its own cell or the eight around it.
  const cellOf = (value: number): number => Math.floor(value / reach);
  const keyOf = (column: number, row: number): number => column * CELL_KEY_ROWS + row;
  const cells = new Map<number, number[]>();
  for (let i = 0; i < data.n; i++) {
    const key = keyOf(cellOf(data.x[i] ?? 0), cellOf(data.y[i] ?? 0));
    const cell = cells.get(key);
    if (cell) cell.push(i);
    else cells.set(key, [i]);
  }
  const counts = kept.map((i) => {
    const x = data.x[i] ?? 0;
    const y = data.y[i] ?? 0;
    const column = cellOf(x);
    const row = cellOf(y);
    let count = 0;
    for (let dy = -1; dy <= 1; dy++)
      for (let dx = -1; dx <= 1; dx++)
        for (const j of cells.get(keyOf(column + dx, row + dy)) ?? []) {
          const ox = (data.x[j] ?? 0) - x;
          const oy = (data.y[j] ?? 0) - y;
          if (j !== i && ox * ox + oy * oy <= reachSquared) count++;
        }
    return count;
  });
  // What an inner dot counts: most of a group is inside, so the 90th percentile.
  const inner = counts.toSorted((a, b) => a - b)[Math.floor(counts.length * 0.9)] || 1;
  const { least, from, to } = shape;
  counts.forEach((count, k) => {
    const i = kept[k]!;
    const share = insideShare(data.x[i] ?? 0, data.y[i] ?? 0, reach, data.w, data.h);
    fade[k] = least + (1 - least) * smoothstep(from, to, count / (inner * share));
  });
  return fade;
}
